import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { forumApi, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ArrowLeft, MessageSquare, Eye, Clock, Pin, Lock, User, ImagePlus, X, Trash2, MoreVertical, Pencil, EyeOff, Settings } from 'lucide-react';
import EmojiReactions from '../components/EmojiReactions';

export default function ThreadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [replyImages, setReplyImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingThread, setEditingThread] = useState(false);
  const [editThreadTitle, setEditThreadTitle] = useState('');
  const [editThreadContent, setEditThreadContent] = useState('');
  const [editingPostId, setEditingPostId] = useState(null);
  const [editPostContent, setEditPostContent] = useState('');
  const fileInputRef = useRef(null);

  const isAdmin = user && (user.role === 'admin' || user.role === 'moderator');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [threadRes, postsRes] = await Promise.all([
          forumApi.getThread(id),
          forumApi.getPosts(id),
        ]);
        setThread(threadRes.data);
        setPosts(postsRes.data);
      } catch (error) {
        console.error('Error fetching thread:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await forumApi.uploadImage(file);
        setReplyImages(prev => [...prev, res.data.image_url]);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() && replyImages.length === 0) return;
    setSubmitting(true);
    setError('');
    try {
      let fullContent = replyContent;
      if (replyImages.length > 0) {
        if (fullContent) fullContent += '\n\n';
        fullContent += replyImages.map(url => `[image]${url}[/image]`).join('\n');
      }
      await forumApi.createPost({ thread_id: id, content: fullContent });
      setReplyContent('');
      setReplyImages([]);
      const postsRes = await forumApi.getPosts(id);
      setPosts(postsRes.data);
      setThread(prev => ({ ...prev, reply_count: prev.reply_count + 1 }));
    } catch (error) {
      setError(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Admin actions
  const handleDeleteThread = async () => {
    if (!window.confirm('Delete this entire thread and all replies?')) return;
    try { await forumApi.deleteThread(id); navigate('/forum'); } catch (err) { alert(formatApiError(err)); }
  };

  const handlePin = async () => {
    try {
      const res = await forumApi.pinThread(id);
      setThread(prev => ({ ...prev, is_pinned: res.data.is_pinned }));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleLock = async () => {
    try {
      const res = await forumApi.lockThread(id);
      setThread(prev => ({ ...prev, is_locked: res.data.is_locked }));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleHideThread = async () => {
    try {
      const res = await forumApi.hideThread(id);
      setThread(prev => ({ ...prev, is_hidden: res.data.is_hidden }));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleEditThread = async () => {
    try {
      await forumApi.editThread(id, { title: editThreadTitle, content: editThreadContent });
      setThread(prev => ({ ...prev, title: editThreadTitle, content: editThreadContent }));
      setEditingThread(false);
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await forumApi.deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      setThread(prev => ({ ...prev, reply_count: prev.reply_count - 1 }));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleEditPost = async (postId) => {
    try {
      await forumApi.editPost(postId, { content: editPostContent });
      setPosts(posts.map(p => p.id === postId ? { ...p, content: editPostContent } : p));
      setEditingPostId(null);
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleHidePost = async (postId) => {
    try {
      const res = await forumApi.hidePost(postId);
      setPosts(posts.map(p => p.id === postId ? { ...p, is_hidden: res.data.is_hidden } : p));
    } catch (err) { alert(formatApiError(err)); }
  };

  const canModerate = (authorId) => user && (user.role === 'admin' || user.role === 'moderator' || user.id === authorId);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const renderPostContent = (content) => {
    if (!content) return null;
    const parts = content.split(/\[image\](.*?)\[\/image\]/g);
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        return (
          <div key={i} className="my-3">
            <img src={part} alt="Forum post" className="max-w-full max-h-[500px] rounded-lg border border-[#262626] object-contain cursor-pointer" onClick={() => window.open(part, '_blank')} />
          </div>
        );
      }
      return part ? <span key={i} className="whitespace-pre-wrap">{part}</span> : null;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400 text-lg mb-4">Thread not found</p>
        <Link to="/forum"><Button className="bg-[#FF3B30] hover:bg-[#E6352B]">Back to Forum</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-6">
          <Link to="/forum" className="inline-flex items-center text-gray-400 hover:text-white mb-4" data-testid="back-to-forum-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Forum
          </Link>
          
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              {thread.is_pinned && <Pin className="w-5 h-5 text-[#FF3B30] mt-1" />}
              {thread.is_locked && <Lock className="w-5 h-5 text-yellow-500 mt-1" />}
              {thread.is_hidden && <EyeOff className="w-5 h-5 text-gray-500 mt-1" />}
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">{thread.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {thread.author_id ? (
                      <Link to={`/profile/${thread.author_id}`} className="text-white hover:text-[#FF3B30] transition-colors" data-testid="thread-author-link">
                        {thread.author_name}
                      </Link>
                    ) : (
                      thread.author_name
                    )}
                  </span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatDate(thread.created_at)}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-4 h-4" />{thread.reply_count} replies</span>
                  <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{thread.view_count} views</span>
                  {thread.is_hidden && <span className="tag bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded">HIDDEN</span>}
                </div>
              </div>
            </div>

            {/* Admin / Author Controls Dropdown */}
            {(isAdmin || (user && thread && user.id === thread.author_id)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-[#262626] text-white" data-testid="thread-admin-menu">
                    <Settings className="w-4 h-4 mr-2" /> {isAdmin ? 'Manage' : 'Edit'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] w-48">
                  <DropdownMenuItem onClick={() => { setEditThreadTitle(thread.title); setEditThreadContent(thread.content); setEditingThread(true); }} className="cursor-pointer" data-testid="edit-thread-action">
                    <Pencil className="w-4 h-4 mr-2" /> Edit Thread
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={handlePin} className="cursor-pointer" data-testid="pin-thread-action">
                        <Pin className="w-4 h-4 mr-2" /> {thread.is_pinned ? 'Unpin' : 'Pin'} Thread
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleLock} className="cursor-pointer" data-testid="lock-thread-action">
                        <Lock className="w-4 h-4 mr-2" /> {thread.is_locked ? 'Unlock' : 'Lock'} Thread
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleHideThread} className="cursor-pointer" data-testid="hide-thread-action">
                        <EyeOff className="w-4 h-4 mr-2" /> {thread.is_hidden ? 'Unhide' : 'Hide'} Thread
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem onClick={handleDeleteThread} className="cursor-pointer text-red-400" data-testid="delete-thread-action">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Thread
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Original Post */}
        <div className={`bg-[#141414] border ${thread.is_hidden ? 'border-gray-600 opacity-60' : 'border-[#262626]'} rounded mb-4`}>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded bg-[#FF3B30] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {thread.author_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  {thread.author_id ? (
                    <Link to={`/profile/${thread.author_id}`} className="font-medium text-white hover:text-[#FF3B30] transition-colors" data-testid="thread-op-link">
                      {thread.author_name}
                    </Link>
                  ) : (
                    <span className="font-medium text-white">{thread.author_name}</span>
                  )}
                  <span className="tag bg-[#262626] text-gray-400 px-2 py-0.5 rounded">OP</span>
                </div>
                <div className="text-gray-300">{renderPostContent(thread.content)}</div>
                <EmojiReactions targetType="thread" targetId={thread.id} />
              </div>
            </div>
          </div>
        </div>

        {/* Replies */}
        {posts.length > 0 && (
          <div className="space-y-4 mb-8">
            <h3 className="tag text-gray-400">Replies ({posts.length})</h3>
            {posts.map((post, index) => (
              <div key={post.id} className={`bg-[#141414] border ${post.is_hidden ? 'border-gray-600 opacity-60' : 'border-[#262626]'} rounded`} data-testid={`forum-post-${post.id}`}>
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded bg-[#262626] flex items-center justify-center text-white font-bold flex-shrink-0">
                      {post.author_name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {post.author_id ? (
                            <Link to={`/profile/${post.author_id}`} className="font-medium text-white hover:text-[#FF3B30] transition-colors" data-testid={`post-author-link-${post.id}`}>
                              {post.author_name}
                            </Link>
                          ) : (
                            <span className="font-medium text-white">{post.author_name}</span>
                          )}
                          {post.author_id === thread.author_id && (
                            <span className="tag bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded">OP</span>
                          )}
                          {post.is_hidden && <span className="tag bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded">HIDDEN</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">#{index + 1} • {formatDate(post.created_at)}</span>
                          {canModerate(post.author_id) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="text-gray-600 hover:text-white transition-colors" data-testid={`post-menu-${post.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626]">
                                <DropdownMenuItem onClick={() => { setEditingPostId(post.id); setEditPostContent(post.content.replace(/\[image\].*?\[\/image\]/g, '').trim()); }} className="cursor-pointer">
                                  <Pencil className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                {isAdmin && (
                                  <DropdownMenuItem onClick={() => handleHidePost(post.id)} className="cursor-pointer">
                                    <EyeOff className="w-4 h-4 mr-2" /> {post.is_hidden ? 'Unhide' : 'Hide'}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-[#262626]" />
                                <DropdownMenuItem onClick={() => handleDeletePost(post.id)} className="cursor-pointer text-red-400">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      {editingPostId === post.id ? (
                        <div className="space-y-3">
                          <Textarea value={editPostContent} onChange={(e) => setEditPostContent(e.target.value)} rows={4} className="bg-[#1F1F1F] border-[#262626] text-white" />
                          <div className="flex gap-2">
                            <Button onClick={() => handleEditPost(post.id)} size="sm" className="bg-[#FF3B30] hover:bg-[#E6352B]">Save</Button>
                            <Button onClick={() => setEditingPostId(null)} size="sm" variant="ghost" className="text-gray-400">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-300">{renderPostContent(post.content)}</div>
                      )}
                      <EmojiReactions targetType="post" targetId={post.id} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reply Form */}
        {user ? (
          thread.is_locked ? (
            <div className="bg-[#141414] border border-[#262626] rounded p-6 text-center">
              <Lock className="w-8 h-8 mx-auto text-gray-500 mb-2" />
              <p className="text-gray-400">This thread is locked. No new replies can be added.</p>
              {isAdmin && (
                <Button onClick={handleLock} variant="outline" size="sm" className="mt-3 border-[#262626] text-white">
                  <Lock className="w-4 h-4 mr-2" /> Unlock Thread
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-[#141414] border border-[#262626] rounded p-6">
              <h3 className="font-heading font-bold text-white mb-4">Post a Reply</h3>
              {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
              <form onSubmit={handleSubmitReply}>
                <Textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} placeholder="Share your thoughts..." rows={4} className="bg-[#1F1F1F] border-[#262626] text-white focus:border-[#FF3B30] mb-4" data-testid="reply-textarea" />
                {replyImages.length > 0 && (
                  <div className="flex flex-wrap gap-3 mb-4">
                    {replyImages.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={url} alt="" className="w-24 h-24 object-cover rounded border border-[#262626]" />
                        <button type="button" onClick={() => setReplyImages(prev => prev.filter((_, i) => i !== idx))} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" multiple className="hidden" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-[#262626] text-gray-300 hover:text-white" data-testid="attach-image-btn">
                    <ImagePlus className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Add Photo'}
                  </Button>
                  <Button type="submit" disabled={submitting || (!replyContent.trim() && replyImages.length === 0)} className="bg-[#FF3B30] hover:bg-[#E6352B] disabled:opacity-50" data-testid="submit-reply-btn">
                    {submitting ? 'Posting...' : 'Post Reply'}
                  </Button>
                </div>
              </form>
            </div>
          )
        ) : (
          <div className="bg-[#141414] border border-[#262626] rounded p-6 text-center">
            <p className="text-gray-400 mb-4">You must be logged in to reply.</p>
            <Link to="/login"><Button className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="login-to-reply-btn">Login to Reply</Button></Link>
          </div>
        )}
      </div>

      {/* Edit Thread Dialog */}
      <Dialog open={editingThread} onOpenChange={setEditingThread}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Thread</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400">Title</label>
              <Input value={editThreadTitle} onChange={(e) => setEditThreadTitle(e.target.value)} className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="edit-thread-title" />
            </div>
            <div>
              <label className="text-sm text-gray-400">Content</label>
              <Textarea value={editThreadContent} onChange={(e) => setEditThreadContent(e.target.value)} rows={6} className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="edit-thread-content" />
            </div>
            <Button onClick={handleEditThread} className="w-full bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="save-thread-edit-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
